/* eslint-disable max-lines */
import createReducer from '../../reducers/utils/createReducer';
import * as c from './types';
import { ErrorType, MachineOption } from '../../types';
import { AnyTypeToFix } from '../../types/anytype';
import { AppLocalStorageValues } from '_clean-code/utils/localStorageHelper';
import _ from 'lodash';
import { IUserData } from 'types/user';

export interface SavedMachineGroup {
  id: string;
  machines: MachineOption[];
}

type SelectedMachines = (MachineOption & { machineGroupId: string })[];

export interface IUserCompany {
  timezone: string;
  companyName: string;
  companyId: number;
}

export type UserStateType = {
  isLoading: boolean;
  isLoaded: boolean;
  error: ErrorType;
  data: IUserData;
  machineGroups: {
    isLoading: boolean;
    allowAllMachines: boolean;
    selectedMachineGroups: SavedMachineGroup[];
    list: {
      name: string;
      id: string;
    }[];
    selectedMachines: SelectedMachines;
  };
  companies?: IUserCompany[];
};

const initialState: Readonly<UserStateType> = {
  isLoading: false,
  isLoaded: false,
  error: null,
  data: {
    email: 'user@example.com',
    timezone: 'UTC',
    companies: [
      {
        timezone: 'utc',
        name: 'pulsar',
        id: 1,
        autoExcludeZeroShifts: false,
        salesPlan: 'trial',
        type: 'company',
        createdAt: '2024-01-01',
        updatedAt: '2024-01-01'
      }
    ],
    id: 1,
    role: 'admin',
    companyId: undefined
  },
  machineGroups: {
    isLoading: false,
    allowAllMachines: false,
    selectedMachineGroups: [],
    list: [],
    selectedMachines: []
  },
  companies: undefined
};

const userReducer = createReducer(initialState, {
  [c.GET_USER_PROFILE_REQUEST]: (state = initialState): UserStateType => ({
    ...state,
    data: {},
    error: null,
    isLoading: true
  }),
  [c.GET_USER_PROFILE_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: IUserData }
  ): UserStateType => ({
    ...state,
    isLoading: false,
    data: {
      ...state.data,
      ...action.payload
    }
  }),
  [c.GET_USER_PROFILE_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ): UserStateType => ({
    ...state,
    error: action.payload,
    isLoading: false
  }),
  [c.GET_CURRENT_USER_MACHINE_GROUPS_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: { name: string; id: string }[] }
  ): UserStateType => {
    if (!action.payload) return state;
    return {
      ...state,
      machineGroups: {
        ...state.machineGroups,
        selectedMachineGroups: [],
        isLoading: false,
        list: action.payload,
        selectedMachines: [] as SelectedMachines
      }
    };
  },
  [c.GET_CURRENT_USER_MACHINE_GROUPS_ERROR]: (
    state = initialState
  ): UserStateType => {
    return {
      ...state,
      machineGroups: {
        ...state.machineGroups,
        selectedMachineGroups: [],
        isLoading: false,
        list: [],
        selectedMachines: []
      }
    };
  },

  [c.SELECT_MACHINE_GROUPS]: (
    state = initialState,
    action: {
      type: string;
      payload?: {
        machineGroups: SavedMachineGroup[];
        selectedMachines: SelectedMachines;
      };
    }
  ): UserStateType => {
    if (!action.payload) return state;
    return {
      ...state,
      machineGroups: {
        ...state.machineGroups,
        selectedMachineGroups: action.payload.machineGroups,
        selectedMachines: _.uniqBy(
          action.payload.selectedMachines,
          'value'
        ) as SelectedMachines
      }
    };
  },

  [c.SELECT_MACHINE_GROUP_MACHINES]: (
    state = initialState,
    action: { type: string; payload?: SelectedMachines }
  ): UserStateType => {
    if (!action.payload) return state;
    return {
      ...state,
      machineGroups: {
        ...state.machineGroups,
        selectedMachines: action.payload
      }
    };
  },

  [c.GET_USER_PROFILE_DATA_REQUEST]: (state = initialState): UserStateType => ({
    ...state,
    data: {},
    error: null,
    isLoading: true
  }),
  [c.GET_USER_PROFILE_DATA_SUCCESS]: (
    state = initialState,
    { payload }: AnyTypeToFix
  ): UserStateType => {
    let selectedMachineGroups: SavedMachineGroup[] = [];
    let selectedMachines: (MachineOption & {
      machineGroupId: string;
    })[] = [];
    const appLocalStorage = new AppLocalStorageValues(
      payload.userData.companyId
    );
    const cachedSelectedMachines = appLocalStorage.globalMachinesGroupMachines;
    if (appLocalStorage.globalMachinesGroups) {
      if (
        appLocalStorage.globalMachinesGroups.length > 0 &&
        appLocalStorage.globalMachinesGroups[0].id === 'all-machines'
      ) {
        selectedMachines = cachedSelectedMachines ? cachedSelectedMachines : [];
        selectedMachineGroups = [
          {
            id: 'all-machines',
            machines: selectedMachines
          }
        ];
      }
      appLocalStorage.globalMachinesGroups.forEach((e) => {
        if (payload.machinesGroups?.groups?.some((x) => x.id === e.id)) {
          selectedMachineGroups.push(e);
        }
      });
      selectedMachines = cachedSelectedMachines ? cachedSelectedMachines : [];
      if (selectedMachineGroups.length === 0) {
        if (payload.machinesGroups?.allMachines) {
          selectedMachines = payload.machinesGroups?.groups
            ? payload.machines.map((machine) => ({
                label: machine.title,
                value: machine.id,
                order: machine.order
              }))
            : [];
          selectedMachineGroups = [
            { id: 'all-machines', machines: selectedMachines }
          ];
        } else {
          selectedMachines = payload.machinesGroups
            ? payload.machinesGroups.groups[0].machines.map((machine) => ({
                label: machine.title,
                value: machine.id,
                order: machine.order
              }))
            : [];
          selectedMachineGroups = [
            {
              id: payload.machinesGroups?.groups[0].id,
              machines: selectedMachines
            }
          ];
        }
      }
    } else if (payload.machinesGroups.allMachines) {
      selectedMachines = cachedSelectedMachines
        ? cachedSelectedMachines
        : payload.machinesGroups?.groups
        ? payload.machines.map((machine) => ({
            label: machine.title,
            value: machine.id,
            order: machine.order
          }))
        : [];
      selectedMachineGroups = [
        { id: 'all-machines', machines: selectedMachines }
      ]; //It means all machines
    } else if (payload.machinesGroups?.groups?.length > 0) {
      selectedMachines = payload.machinesGroups
        ? payload.machinesGroups.groups[0].machines.map((machine) => ({
            label: machine.title,
            value: machine.id,
            order: machine.order
          }))
        : [];
      selectedMachineGroups = [
        {
          id: payload.machinesGroups?.groups[0].id,
          machines: selectedMachines
        }
      ];
    } else {
      selectedMachineGroups = [];
    }

    return {
      ...state,
      isLoading: false,
      isLoaded: true,
      data: payload.userData,
      machineGroups: {
        ...state.machineGroups,
        allowAllMachines: payload.machinesGroups.allMachines,
        list: payload.machinesGroups.groups,
        selectedMachineGroups,
        selectedMachines:
          cachedSelectedMachines?.length > 0
            ? _.uniqBy(cachedSelectedMachines, 'value')
            : _.uniqBy(
                selectedMachineGroups
                  .map((e) =>
                    e.machines.map((machine) => ({
                      ...machine,
                      machineGroupId: e.id
                    }))
                  )
                  .flat(1),
                'value'
              )
      }
    };
  },
  [c.GET_USER_COMPANIES_SUCCESS]: (
    state = initialState,
    { payload }: any
  ): UserStateType => ({
    ...state,
    companies: payload
  }),
  [c.GET_USER_PROFILE_DATA_ERROR]: (
    state = initialState,
    { payload }: AnyTypeToFix
  ): UserStateType => ({
    ...state,
    error: payload,
    isLoading: false,
    isLoaded: true
  })
});

export default userReducer;
